/**
 * Select Component implemented with Material UI
 * 
 * This component provides a dropdown select control with customizable styling and options.
 */
import React from 'react';
import { Select as MuiSelectBase, MenuItem as MuiMenuItem, styled, SelectProps as MuiSelectProps } from '@mui/material';
import { SelectProps } from 'types/components/atoms';

// Interface for the styled select component props
interface StyledSelectProps extends Omit<MuiSelectProps, 'classes'> {
  isNav?: boolean;
  dim?: boolean;
}

/**
 * Select component
 * 
 * @param props - Component props
 * @returns A customized dropdown select component
 */
const Select: React.FC<SelectProps> = (props) => {
  const { items = [], value, placeHolder, unit, isNav, onChange, show, bubbles, ...otherProps } = props;
  
  return (
    <StyledSelect
      value={value || placeHolder || ''}
      onChange={onChange}
      displayEmpty
      isNav={isNav}
      dim={!value}
      MenuProps={{
        PaperProps: {
          sx: (theme) => ({
            backgroundColor: theme.palette.background.paper,
            border: theme.palette.mode === 'light'
              ? '0.6px solid rgba(17, 17, 17, 0.15)'
              : `0.6px solid ${theme.palette.divider}`,
            borderRadius: '4px',
            boxShadow: theme.shadows[1],
            marginTop: '4px',
          }),
        },
      }}
      {...otherProps as MuiSelectProps}
    >
      {placeHolder && !value && (
        <StyledMenuItem disabled value={placeHolder}>
          {placeHolder}
        </StyledMenuItem>
      )}
      {items.map((item, index) => {
        const rawText = bubbles?.[item] || String(item).replaceAll('_', ' ');
        const displayText = rawText.charAt(0).toUpperCase() + rawText.slice(1);
        return (
          <StyledMenuItem key={`${item}-${index}`} value={item}>
            {displayText.concat(unit || '')}
          </StyledMenuItem>
        );
      })}
    </StyledSelect>
  );
};

// Styled MenuItem with Figma theme tokens
const StyledMenuItem = styled(MuiMenuItem)(({ theme }) => ({
  fontSize: '12px !important',
  fontFamily: theme.typography.fontFamily,
  color: theme.palette.text.primary,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  '&.Mui-selected': {
    backgroundColor: theme.palette.action.selected,
    '&:hover': {
      backgroundColor: theme.palette.action.selected,
    },
  },
  '&.Mui-disabled': {
    color: theme.palette.text.disabled,
    opacity: 0.6,
  },
}));

// Styled MUI Select component with Figma theme tokens
const StyledSelect = styled(MuiSelectBase, {
  shouldForwardProp: (prop) => !['isNav', 'dim', 'show'].includes(prop as string),
})<StyledSelectProps>(({ theme, isNav, dim }) => ({
  border: theme.palette.mode === 'light'
    ? '0.6px solid rgba(17, 17, 17, 0.15)'
    : `0.6px solid ${theme.palette.divider}`,
  backgroundColor: `${theme.palette.background.default} !important`,
  height: '36px',
  outline: 'none',
  width: '100%',
  minWidth: isNav ? '80px' : '140px',
  borderRadius: '4px',
  opacity: dim ? 0.5 : 1,
  color: theme.palette.text.primary,
  fontSize: '12px !important',
  fontFamily: theme.typography.fontFamily,
  '& .MuiSelect-select': {
    padding: '0 24px 0 8px',
    height: '36px',
    lineHeight: '36px',
    minHeight: 'unset',
    color: theme.palette.text.primary,
    fontSize: '12px !important',
  },
  '& .MuiSelect-icon': {
    color: theme.palette.text.secondary,
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
  '&:hover': {
    backgroundColor: theme.palette.background.default,
    border: theme.palette.mode === 'light'
      ? '0.6px solid rgba(17, 17, 17, 0.25)'
      : `0.6px solid ${theme.palette.action.hover}`,
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
  },
  '&.Mui-focused': {
    backgroundColor: theme.palette.background.default,
    border: `1px solid ${theme.palette.primary.main}`,
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
  },
  '&.Mui-disabled': {
    backgroundColor: theme.palette.background.default,
    color: theme.palette.text.disabled,
    border: `0.6px solid ${theme.palette.action.disabled}`,
    opacity: 0.6,
    '& .MuiSelect-icon': {
      color: theme.palette.text.disabled,
    },
  },
}));

export type { SelectProps };
export default Select;
